import { Box, Typography, Stack, Alert, useTheme, Link } from "@mui/material"
import { Constants } from "../../../Models/Constants"
import { getFlowTypeUnits, getPreferredUnits } from "../../../Services/Global";
import { isRelevantInputField } from "../../../Utils/UnitConfigurationHelper";
import { UnitType, formatUnitValue } from "../../../Utils/UnitsHelper";
import { useEffect, useState } from "react";
import { IMultiSectionUnitConfiguration, IUnitConfiguration } from "../../../Models/IUnitConfiguration";
import { IMultiSectionProperties } from "../../../Models/IMultiSectionProperties";
import React from "react";
import { UnitAwareTextField } from "../../Atoms/UnitAwareTextField";
import { useMsal } from "@azure/msal-react";
import { ApiService } from "../../../Services/ApiService";
import { IDesignRangeFeedback } from "../../../Models/IDesignRangeFeedback";
import { useFormDataCollector, IExplicitFormData } from "../../../hooks/useFormDataCollector";
import { RangeFeedbackDialog } from "../../Molecules/Feedback/RangeFeedbackDialog";

/**
 * Interface for value range constraints for input fields
 */
export interface IValueRange {
    hotFlowRate?: any;
    hotInlet?: any;
    hotOutlet?: any;
    hotPressureDrop?: any;
    coldInlet?: any;
    coldOutlet?: any;
    coldFlowRate?: any;
    coldPressureDrop?: any;
}

/**
 * ConditionalTextFieldInputs component (Material UI version)
 *
 * @export
 * @param {*} {
 *     disabled,
 *     originPage,
 *     unitConfiguration,
 *     propertySpecs,
 *     handleChange,
 *     hotMediaFlowrate,
 *     hotMediaTempIn,
 *     hotMediaTempOut,
 *     hotMediaPressureDrop,
 *     coldMediaFlowrate,
 *     coldMediaTempIn,
 *     coldMediaTempOut,
 *     coldMediaPressureDrop,
 *     hotMediaFlowrateError,
 *     hotMediaTempOutError,
 *     hotMediaTempInError,
 *     hotMediaPressureDropError,
 *     coldMediaFlowrateError,
 *     coldMediaTempInError,
 *     coldMediaTempOutError,
 *     coldMediaPressureDropError,
 *     addStyling,
 *     readOnlyFields,
 *     temperatureError,
 *     valuesRange
 * }
 * @return {*} 
 */
export const ConditionalTextFieldInputs: React.FC<{
    disabled?: boolean,
    originPage: string,
    unitConfiguration: IUnitConfiguration | IMultiSectionUnitConfiguration,
    propertySpecs?: IMultiSectionProperties[],
    handleChange: any,
    hotMediaFlowrate?: string,
    hotMediaTempIn?: string,
    hotMediaTempOut?: string,
    hotMediaPressureDrop?: string,
    coldMediaFlowrate?: string,
    coldMediaTempIn?: string,
    coldMediaTempOut?: string,
    coldMediaPressureDrop?: string,
    hotMediaFlowrateError?: string,
    hotMediaTempOutError?: string,
    hotMediaTempInError?: string,
    hotMediaPressureDropError?: string,
    coldMediaFlowrateError?: string,
    coldMediaTempInError?: string,
    coldMediaTempOutError?: string,
    coldMediaPressureDropError?: string,
    addStyling?: boolean,
    readOnlyFields?: string[],
    temperatureError?: string[], // Changed from any to string[] to handle multiple errors
    valuesRange?: IValueRange,
    formKey?: string // Unique key for form to prevent browser autocomplete between different forms
}> = ({
    disabled = false,
    originPage,
    unitConfiguration,
    formKey = '',
    propertySpecs,
    handleChange,
    hotMediaFlowrate,
    hotMediaTempIn,
    hotMediaTempOut,
    hotMediaPressureDrop,
    coldMediaFlowrate,
    coldMediaTempIn,
    coldMediaTempOut,
    coldMediaPressureDrop,
    hotMediaFlowrateError,
    hotMediaTempInError,
    hotMediaTempOutError,
    hotMediaPressureDropError,
    coldMediaTempInError,
    coldMediaTempOutError,
    coldMediaFlowrateError,
    coldMediaPressureDropError,
    addStyling,
    readOnlyFields,
    temperatureError,
    valuesRange,
}) => {

    const unitOperationTargetValueProp = propertySpecs && propertySpecs.find(c => c.name === Constants.UnitOperationTargetValue);

    const shouldDisplayUnitOperationTargetValue = unitOperationTargetValueProp !== undefined && unitOperationTargetValueProp !== null;

    const [flowUnit, setFlowUnit] = useState('');
    const [shouldStyleHot, setShouldStyleHot] = useState(false);
    const [shouldStyleCold, setShouldStyleCold] = useState(false);

    const theme = useTheme();
    const warmStyling = {
        border: `2px solid ${theme.palette.error.main}`,
        borderRadius: 1,
        padding: 2,
        mb: 2
    };

    const coldStyling = {
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: 1,
        padding: 2,
        mb: 2
    };

    const preferredUnit = getPreferredUnits().preferredUnits;
    const temperatureUnit = preferredUnit === 'us' ? '°F' : '°C';

    const pressureUnit = preferredUnit === 'us' ? 'PSI' : 'kPa';

    useEffect(() => {
        if (unitConfiguration === undefined || unitConfiguration.flowType === null) return;

        let shouldStyleHot = false;
        let shouldStyleCold = false;

        if (!unitConfiguration.isMultiSection) {
            const hotInputs = unitConfiguration.inputs?.filter(x => x.startsWith('Hot'));
            const coldInputs = unitConfiguration.inputs?.filter(x => x.startsWith('Cold'));

            shouldStyleHot = hotInputs && hotInputs.length > 0 ? true : false;
            shouldStyleCold = coldInputs && coldInputs.length > 0 ? true : false;
        }
        else {
            if (propertySpecs) {
                const hotPropertySpecs = propertySpecs.filter(x => x.name.startsWith('Hot'));
                shouldStyleHot = hotPropertySpecs && hotPropertySpecs.length > 0 ? true : false;

                const coldPropertySpecs = propertySpecs.filter(x => x.name.startsWith('Cold'));
                shouldStyleCold = coldPropertySpecs && coldPropertySpecs.length > 0 ? true : false;
            }
        }

        setFlowUnit(getFlowTypeUnits(unitConfiguration.flowType!));
        setShouldStyleHot(shouldStyleHot);
        setShouldStyleCold(shouldStyleCold);

    }, [unitConfiguration, propertySpecs]);

    // State and hooks for design range feedback
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const msalContext = useMsal();
    const apiService = new ApiService(msalContext);

    // Collect explicit values from props for Single Section scenarios
    const explicitValues: IExplicitFormData = {
        hotMediaFlowrate,
        hotMediaTempIn,
        hotMediaTempOut,
        hotMediaPressureDrop,
        coldMediaFlowrate,
        coldMediaTempIn,
        coldMediaTempOut,
        coldMediaPressureDrop
    };

    const collectFormData = useFormDataCollector(
        unitConfiguration as IMultiSectionUnitConfiguration, 
        propertySpecs,
        explicitValues
    );

    /**
     * Identifies which inputs have validation errors for the current feedback context
     * @param sectionNumber - Optional section number for filtering in multi-section mode
     */
    const getViolatedInputs = (sectionNumber?: number): string => {
        const violations: string[] = [];

        if (unitConfiguration?.isMultiSection) {
            // Multi-Section: check propertySpecs for specific section
            propertySpecs?.forEach(prop => {
                // Check if property belongs to this section and has error
                if (prop.sectionNumber === sectionNumber && prop.errorMessage) {
                    violations.push(prop.trimmedName || prop.name);
                }
            });

            // Special check for UnitOperationTargetValue (it belongs to global/header, usually section 0)
            // We attach it to Section 1 so it gets captured at least once
            if (sectionNumber === 1) {
                const targetProp = propertySpecs?.find(p => p.name === Constants.UnitOperationTargetValue);
                if (targetProp && targetProp.errorMessage) {
                    violations.push(Constants.UnitOperationTargetValue);
                }
            }
        } else {
            // Single-Section: check individual error props
            if (hotMediaFlowrateError) violations.push(Constants.HotMediaFlowrate);
            if (hotMediaTempInError) violations.push(Constants.HotMediaTempIn);
            if (hotMediaTempOutError) violations.push(Constants.HotMediaTempOut);
            if (hotMediaPressureDropError) violations.push(Constants.HotMediaPressureDrop);
            
            if (coldMediaFlowrateError) violations.push(Constants.ColdMediaFlowrate);
            if (coldMediaTempInError) violations.push(Constants.ColdMediaTempIn);
            if (coldMediaTempOutError) violations.push(Constants.ColdMediaTempOut);
            if (coldMediaPressureDropError) violations.push(Constants.ColdMediaPressureDrop);
        }

        return violations.length > 0 ? violations.join(', ') : '';
    };

    /**
     * Handles feedback submission when user clicks the feedback link
     * Collects form data and sends it to the backend API
     * For Multi-Section, it saves a record for EACH section to capture the full state.
     */
    const handleFeedbackClick = async (clickedSectionNumber?: number) => {
        try {
            if (unitConfiguration?.isMultiSection) {
                const multiConfig = unitConfiguration as IMultiSectionUnitConfiguration;
                
                if (multiConfig.sectionCount) {
                    // Multi-Section: Iterate through all sections and save them
                    const promises = [];
                    for (let i = 1; i <= multiConfig.sectionCount; i++) {
                        const formData = collectFormData(i);
                        const violatedInputs = getViolatedInputs(i);
                        
                        console.log(`Sending Design Range Feedback (Section ${i}):`, { ...formData, violatedInputs });
                        
                        const feedback: IDesignRangeFeedback = {
                            ...formData as IDesignRangeFeedback,
                            violatedInputs: violatedInputs
                        };
                        
                        promises.push(apiService.submitDesignRangeFeedback(feedback));
                    }
                    
                    await Promise.all(promises);
                }
            } else {
                // Single Section (or fallback)
                const formData = collectFormData(clickedSectionNumber);
                const violatedInputs = getViolatedInputs(clickedSectionNumber);
                
                console.log('Sending Design Range Feedback:', { ...formData, violatedInputs });
                
                const feedback: IDesignRangeFeedback = {
                    ...formData as IDesignRangeFeedback,
                    violatedInputs: violatedInputs
                };
                
                await apiService.submitDesignRangeFeedback(feedback);
            }
            
            setFeedbackDialogOpen(true);
        } catch (error) {
            console.error('Failed to submit design range feedback:', error);
            // Fallback to mailto in case of error
            const feedbackMessage = `Regarding the ${unitConfiguration.productLine} ${unitConfiguration.alfaLavalPosition},\nI wish for a wider design window.\nHere is what I would need:\n\n[Add your feedback here]\n\n`;
            window.location.href = `mailto:AIDesignWizardFeedback@alfalaval.com?subject=[EMELIE][Feedback] - Design Window Feedback&body=${encodeURIComponent(feedbackMessage)}`;
        }
    };

    /**
     * Render error message for plain text (returns JSX)
     * Updated to support both mailto fallback and API-based feedback submission
     */
    function renderErrorMessage(error?: string, sectionNumber?: number) {
        if (error === undefined || error === '') return null;

        return (
            <>
                {error} Please{' '}
                <Link 
                    sx={{ color: theme.palette.primary.main, cursor: 'pointer' }} 
                    onClick={() => handleFeedbackClick(sectionNumber)}
                >
                    <b>send feedback</b>
                </Link>
                {' '}on which ranges you need for future implementations.
            </>
        );
    }

    /**
     * Render tooltip message for plain text (returns String)
     * Enhanced with fallbacks to ensure tooltips are always shown when range data is available
     */
    function renderTooltipContent(min: number | undefined, max: number | undefined, unitType: UnitType | null, rangeData?: any, unitLabel?: string) {
        // Helper to safely convert to number and check validity
        const toValidNumber = (val: number | undefined | null): number | undefined => {
            if (val === undefined || val === null) return undefined;
            const num = typeof val === 'string' ? parseFloat(val) : val;
            return !isNaN(num) ? num : undefined;
        };

        // A dimensionless quantity has no UnitType to derive a suffix from, so
        // the configured label is appended explicitly.
        const fmt = (value: number): string =>
            unitType === null && unitLabel
                ? `${formatUnitValue(value, null, false)} ${unitLabel}`
                : formatUnitValue(value, unitType, false);

        const validMin = toValidNumber(min);
        const validMax = toValidNumber(max);

        // Check if we have enhanced range data that considers temperature constraints
        if (rangeData?.hasTemperatureConstraint) {
            const enhancedMin = toValidNumber(rangeData.realAcceptableMin);
            const enhancedMax = toValidNumber(rangeData.realAcceptableMax);
            
            if (enhancedMin !== undefined && enhancedMax !== undefined) {
                return `The value must be within the range of ${fmt(enhancedMin)} and ${fmt(enhancedMax)}.`;
            }
        }

        // Full range available
        if (validMin !== undefined && validMax !== undefined) {
            return `The value must be within the range of ${fmt(validMin)} and ${fmt(validMax)}.`;
        }

        // Only minimum available
        if (validMin !== undefined) {
            return `The value must be at least ${fmt(validMin)}.`;
        }

        // Only maximum available
        if (validMax !== undefined) {
            return `The value must be at most ${fmt(validMax)}.`;
        }

        // No range data available
        return '';
    }

    // Calculate hotProperties and coldProperties directly from propertySpecs for multi-section forms
    // This ensures values are always synchronized with the parent component's state
    const hotPropertySpecs = unitConfiguration?.isMultiSection && propertySpecs 
        ? propertySpecs.filter(x => x.name.startsWith('Hot'))
        : [];
    
    const coldPropertySpecs = unitConfiguration?.isMultiSection && propertySpecs 
        ? propertySpecs.filter(x => x.name.startsWith('Cold'))
        : [];

    return (
        <Stack spacing={2}>
            {
                (originPage == Constants.ModelDataSampleForm || originPage == Constants.DesignSessionReviewForm || (originPage == Constants.ProductDesignForm && !unitConfiguration.isMultiSection)) ?
                    <>
                        <Box sx={addStyling || shouldStyleHot ? warmStyling : {}} display="flex" flexDirection="column" gap={1}>
                            {/* Hot media mass flowrate */}
                            {
                                isRelevantInputField(Constants.HotMediaFlowrate, unitConfiguration) &&
                                <Box>
                                    <Typography variant="body2" gutterBottom>
                                        {unitConfiguration.hotMediaType} flow rate
                                    </Typography>
                                    {
                                        readOnlyFields?.includes(Constants.HotMediaFlowrate) ?
                                            <Typography>{formatUnitValue(hotMediaFlowrate, unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow', false)}</Typography>
                                            :
                                            <UnitAwareTextField 
                                                unitType={unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow'}
                                                key={`${formKey}-${Constants.HotMediaFlowrate}`}
                                                type='number' 
                                                disabled={disabled} 
                                                inputProps={{ step: 1 }} 
                                                onMetricValueChange={(val) => handleChange({ target: { name: Constants.HotMediaFlowrate, value: val } })} 
                                                name={Constants.HotMediaFlowrate} 
                                                metricValue={hotMediaFlowrate} 
                                                InputProps={{ 
                                                    endAdornment: <Typography variant="caption" color="text.secondary">{flowUnit}</Typography> 
                                                }}
                                                helperText={renderErrorMessage(hotMediaFlowrateError)} 
                                                error={!!hotMediaFlowrateError}
                                                fullWidth
                                                size="small"
                                                tooltipContent={renderTooltipContent(valuesRange?.hotFlowRate?.lessThan, valuesRange?.hotFlowRate?.greaterThan, unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow', valuesRange?.hotFlowRate)}
                                            />
                                    }
                                </Box>
                            }

                            {/* Hot inlet and outlet temperature */}
                            {
                                // If both temperature in and out are readonly and relevant, then combine the two into one readonly property.
                                isRelevantInputField(Constants.HotMediaTempIn, unitConfiguration) && isRelevantInputField(Constants.HotMediaTempOut, unitConfiguration)
                                    && readOnlyFields?.includes(Constants.HotMediaTempIn) && readOnlyFields.includes(Constants.HotMediaTempOut) ?
                                    <Box>
                                        <Typography variant="body2" gutterBottom>
                                            {unitConfiguration.hotMediaType} temperature
                                        </Typography>
                                        <Typography variant="body1" color="error.main">{formatUnitValue(hotMediaTempIn, 'temperature', false)}</Typography>
                                    </Box>
                                    :
                                    <>
                                        {
                                            isRelevantInputField(Constants.HotMediaTempIn, unitConfiguration) &&
                                            <Box>
                                                <Typography variant="body2" gutterBottom>
                                                    {unitConfiguration.hotMediaType} temperature inlet
                                                </Typography>
                                                {
                                                    readOnlyFields?.includes(Constants.HotMediaTempIn) ?
                                                        <Typography variant="body1">{formatUnitValue(hotMediaTempIn, 'temperature', false)}</Typography>
                                                        :
                                                        <UnitAwareTextField 
                                                            unitType="temperature"
                                                            key={`${formKey}-${Constants.HotMediaTempIn}`}
                                                            type='number' 
                                                            disabled={disabled} 
                                                            inputProps={{ step: 0.1 }} 
                                                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.HotMediaTempIn, value: val } })} 
                                                            name={Constants.HotMediaTempIn} 
                                                            metricValue={hotMediaTempIn} 
                                                            InputProps={{ 
                                                                endAdornment: <Typography variant="caption" color="text.secondary">{temperatureUnit}</Typography> 
                                                            }}
                                                            helperText={renderErrorMessage(hotMediaTempInError)} 
                                                            error={!!hotMediaTempInError}
                                                            fullWidth
                                                            size="small"
                                                            tooltipContent={renderTooltipContent(valuesRange?.hotInlet?.lessThan, valuesRange?.hotInlet?.greaterThan, "temperature", valuesRange?.hotInlet)}
                                                        />
                                                }
                                            </Box>
                                        }

                                        {
                                            isRelevantInputField(Constants.HotMediaTempOut, unitConfiguration) &&
                                            <Box>
                                                <Typography variant="body2" gutterBottom>
                                                    {unitConfiguration.hotMediaType} temperature outlet
                                                </Typography>
                                                {
                                                    readOnlyFields?.includes(Constants.HotMediaTempOut) ?
                                                        <Typography variant="body1">{formatUnitValue(hotMediaTempOut, 'temperature', false)}</Typography>
                                                        :
                                                        <UnitAwareTextField 
                                                            unitType="temperature"
                                                            key={`${formKey}-${Constants.HotMediaTempOut}`}
                                                            type='number' 
                                                            disabled={disabled} 
                                                            inputProps={{ step: 0.1 }} 
                                                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.HotMediaTempOut, value: val } })} 
                                                            name={Constants.HotMediaTempOut} 
                                                            metricValue={hotMediaTempOut} 
                                                            InputProps={{ 
                                                                endAdornment: <Typography variant="caption" color="text.secondary">{temperatureUnit}</Typography> 
                                                            }}
                                                            helperText={renderErrorMessage(hotMediaTempOutError)} 
                                                            error={!!hotMediaTempOutError}
                                                            fullWidth
                                                            size="small"
                                                            tooltipContent={renderTooltipContent(valuesRange?.hotOutlet?.lessThan, valuesRange?.hotOutlet?.greaterThan, "temperature", valuesRange?.hotOutlet)}
                                                        />
                                                }
                                            </Box>
                                        }
                                    </>
                            }

                            {/* Hot media pressure drop */}
                            {
                                isRelevantInputField(Constants.HotMediaPressureDrop, unitConfiguration) &&
                                <Box>
                                    <Typography variant="body2" gutterBottom>
                                        {unitConfiguration.hotMediaType} pressure drop
                                    </Typography>
                                    {
                                        readOnlyFields?.includes(Constants.HotMediaPressureDrop) ?
                                            <Typography variant="body1">{formatUnitValue(hotMediaPressureDrop, 'pressure', false)}</Typography>
                                            :
                                            <UnitAwareTextField 
                                                unitType="pressure"
                                                key={`${formKey}-${Constants.HotMediaPressureDrop}`}
                                                type='number' 
                                                disabled={disabled} 
                                                inputProps={{ step: 0.1 }} 
                                                onMetricValueChange={(val) => handleChange({ target: { name: Constants.HotMediaPressureDrop, value: val } })} 
                                                name={Constants.HotMediaPressureDrop} 
                                                metricValue={`${hotMediaPressureDrop}`} 
                                                InputProps={{ 
                                                    endAdornment: <Typography variant="caption" color="text.secondary">{pressureUnit}</Typography> 
                                                }}
                                                helperText={renderErrorMessage(hotMediaPressureDropError)} 
                                                error={!!hotMediaPressureDropError}
                                                fullWidth
                                                size="small"
                                                tooltipContent={renderTooltipContent(valuesRange?.hotPressureDrop?.lessThan, valuesRange?.hotPressureDrop?.greaterThan, "pressure", valuesRange?.hotPressureDrop)}
                                            />
                                    }
                                </Box>
                            }
                        </Box>

                        {/* Cold media fields */}
                        <Box sx={addStyling || shouldStyleCold ? coldStyling : {}} display="flex" flexDirection="column" gap={1}>
                            {/* Cold media mass flowrate */}
                            {
                                isRelevantInputField(Constants.ColdMediaFlowrate, unitConfiguration) &&
                                <Box>
                                    <Typography variant="body2" gutterBottom>
                                        {unitConfiguration.coldMediaType} flow rate
                                    </Typography>
                                    {
                                        readOnlyFields?.includes(Constants.ColdMediaFlowrate) ?
                                            <Typography>{formatUnitValue(coldMediaFlowrate, unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow', false)}</Typography>
                                            :
                                            <UnitAwareTextField 
                                                unitType={unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow'}
                                                key={`${formKey}-${Constants.ColdMediaFlowrate}`}
                                                type='number' 
                                                disabled={disabled} 
                                                inputProps={{ step: 1 }} 
                                                onMetricValueChange={(val) => handleChange({ target: { name: Constants.ColdMediaFlowrate, value: val } })} 
                                                name={Constants.ColdMediaFlowrate} 
                                                metricValue={coldMediaFlowrate} 
                                                InputProps={{ 
                                                    endAdornment: <Typography variant="caption" color="text.secondary">{flowUnit}</Typography> 
                                                }}
                                                helperText={renderErrorMessage(coldMediaFlowrateError)} 
                                                error={!!coldMediaFlowrateError}
                                                fullWidth
                                                size="small"
                                                tooltipContent={renderTooltipContent(valuesRange?.coldFlowRate?.lessThan, valuesRange?.coldFlowRate?.greaterThan, unitConfiguration?.flowType?.toLowerCase() === 'volumetric' ? 'volumetricFlow' : 'massFlow', valuesRange?.coldFlowRate)}
                                            />
                                    }
                                </Box>
                            }

                            {/* Cold media inlet and outlet temperature */}
                            {
                                // If both temperature in and out are readonly and relevant, then combine the two into one readonly property.
                                isRelevantInputField(Constants.ColdMediaTempIn, unitConfiguration) && isRelevantInputField(Constants.ColdMediaTempOut, unitConfiguration)
                                    && readOnlyFields?.includes(Constants.ColdMediaTempIn) && readOnlyFields.includes(Constants.ColdMediaTempOut) ?
                                    <Box>
                                        <Typography variant="body2" gutterBottom>
                                            {unitConfiguration.coldMediaType} temperature
                                        </Typography>
                                        <Typography variant="body1" color="primary.main">{formatUnitValue(coldMediaTempIn, 'temperature', false)}</Typography>
                                    </Box>
                                    :
                                    <>
                                        {
                                            isRelevantInputField(Constants.ColdMediaTempIn, unitConfiguration) &&
                                            <Box>
                                                <Typography variant="body2" gutterBottom>
                                                    {unitConfiguration.coldMediaType} temperature inlet
                                                </Typography>
                                                {
                                                    readOnlyFields?.includes(Constants.ColdMediaTempIn) ?
                                                        <Typography variant="body1">{formatUnitValue(coldMediaTempIn, 'temperature', false)}</Typography>
                                                        :
                                                        <UnitAwareTextField 
                                                            unitType="temperature"
                                                            key={`${formKey}-${Constants.ColdMediaTempIn}`}
                                                            type='number' 
                                                            disabled={disabled} 
                                                            inputProps={{ step: 0.1 }} 
                                                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.ColdMediaTempIn, value: val } })} 
                                                            name={Constants.ColdMediaTempIn} 
                                                            metricValue={coldMediaTempIn} 
                                                            InputProps={{ 
                                                                endAdornment: <Typography variant="caption" color="text.secondary">{temperatureUnit}</Typography> 
                                                            }}
                                                            helperText={renderErrorMessage(coldMediaTempInError)} 
                                                            error={!!coldMediaTempInError}
                                                            fullWidth
                                                            size="small"
                                                            tooltipContent={renderTooltipContent(valuesRange?.coldInlet?.lessThan, valuesRange?.coldInlet?.greaterThan, "temperature", valuesRange?.coldInlet)}
                                                        />
                                                }
                                            </Box>
                                        }
                                        {
                                            isRelevantInputField(Constants.ColdMediaTempOut, unitConfiguration) &&
                                            <Box>
                                                <Typography variant="body2" gutterBottom>
                                                    {unitConfiguration.coldMediaType} temperature outlet
                                                </Typography>
                                                {
                                                    readOnlyFields?.includes(Constants.ColdMediaTempOut) ?
                                                        <Typography variant="body1">{formatUnitValue(coldMediaTempOut, 'temperature', false)}</Typography>
                                                        :
                                                        <UnitAwareTextField 
                                                            unitType="temperature"
                                                            key={`${formKey}-${Constants.ColdMediaTempOut}`}
                                                            type='number' 
                                                            disabled={disabled} 
                                                            inputProps={{ step: 0.1 }} 
                                                            onMetricValueChange={(val) => handleChange({ target: { name: Constants.ColdMediaTempOut, value: val } })} 
                                                            name={Constants.ColdMediaTempOut} 
                                                            metricValue={coldMediaTempOut} 
                                                            InputProps={{ 
                                                                endAdornment: <Typography variant="caption" color="text.secondary">{temperatureUnit}</Typography> 
                                                            }}
                                                            helperText={renderErrorMessage(coldMediaTempOutError)} 
                                                            error={!!coldMediaTempOutError}
                                                            fullWidth
                                                            size="small"
                                                            tooltipContent={renderTooltipContent(valuesRange?.coldOutlet?.lessThan, valuesRange?.coldOutlet?.greaterThan, "temperature", valuesRange?.coldOutlet)}
                                                        />
                                                }
                                            </Box>
                                        }
                                    </>
                            }

                            {/* Cold media pressure drop */}
                            {
                                isRelevantInputField(Constants.ColdMediaPressureDrop, unitConfiguration) &&
                                <Box>
                                    <Typography variant="body2" gutterBottom>
                                        {unitConfiguration.coldMediaType} pressure drop
                                    </Typography>
                                    {
                                        readOnlyFields?.includes(Constants.ColdMediaPressureDrop) ?
                                            <Typography variant="body1">{formatUnitValue(coldMediaPressureDrop, 'pressure', false)}</Typography>
                                            :
                                            <UnitAwareTextField 
                                                unitType="pressure"
                                                key={`${formKey}-${Constants.ColdMediaPressureDrop}`}
                                                type='number' 
                                                disabled={disabled} 
                                                inputProps={{ step: 0.1 }} 
                                                onMetricValueChange={(val) => handleChange({ target: { name: Constants.ColdMediaPressureDrop, value: val } })} 
                                                name={Constants.ColdMediaPressureDrop} 
                                                metricValue={coldMediaPressureDrop} 
                                                InputProps={{ 
                                                    endAdornment: <Typography variant="caption" color="text.secondary">{pressureUnit}</Typography> 
                                                }}
                                                helperText={renderErrorMessage(coldMediaPressureDropError)} 
                                                error={!!coldMediaPressureDropError}
                                                fullWidth
                                                size="small"
                                                tooltipContent={renderTooltipContent(valuesRange?.coldPressureDrop?.lessThan, valuesRange?.coldPressureDrop?.greaterThan, "pressure", valuesRange?.coldPressureDrop)}
                                            />
                                    }
                                </Box>
                            }
                        </Box>
                    </>
                    :
                    <>
                        {/* Multi-section hot properties */}
                        <Box sx={addStyling && shouldStyleHot ? warmStyling : {}} display="flex" flexDirection="column" gap={1}>
                            {
                                hotPropertySpecs && hotPropertySpecs.map((property, index) => (
                                    <Box key={index + property.name}>
                                        <Typography variant="body2" gutterBottom>
                                            {property.mediaType} {property.property}
                                        </Typography>
                                        {
                                            readOnlyFields?.includes(property.name) ?
                                                <Typography variant="body1">{property.value || ''} {property.flowUnit}</Typography>
                                                :
                                                <UnitAwareTextField
                                                    unitType={property.unitType}
                                                    key={`${formKey}-${index}-${property.name}`}
                                                    type='number'
                                                    onMetricValueChange={(val) => handleChange({ target: { name: property.name, value: val } })}
                                                    disabled={disabled}
                                                    inputProps={{ step: 0.1 }}
                                                    name={property.name}
                                                    metricValue={property.value || ''}
                                                    InputProps={{ 
                                                        endAdornment: <Typography variant="caption" color="text.secondary">{property.flowUnit}</Typography> 
                                                    }}
                                                    helperText={renderErrorMessage(property.errorMessage, property.sectionNumber)}
                                                    error={!!property.errorMessage}
                                                    fullWidth
                                                    size="small"
                                                    tooltipContent={renderTooltipContent(property.lessThan, property.greaterThan, property.unitType)}
                                                />
                                        }
                                    </Box>
                                ))
                            }
                        </Box>

                        {/* Multi-section cold properties */}
                        <Box sx={addStyling && shouldStyleCold ? coldStyling : {}} display="flex" flexDirection="column" gap={1}>
                            {
                                coldPropertySpecs && coldPropertySpecs.map((property, index) => (
                                    <Box key={index + property.name}>
                                        <Typography variant="body2" gutterBottom>
                                            {property.mediaType} {property.property}
                                        </Typography>
                                        {
                                            readOnlyFields?.includes(property.name) ?
                                                <Typography variant="body1">{property.value || ''} {property.flowUnit}</Typography>
                                                :
                                                <UnitAwareTextField
                                                    unitType={property.unitType}
                                                    key={`${formKey}-${index}-${property.name}`}
                                                    type='number'
                                                    onMetricValueChange={(val) => handleChange({ target: { name: property.name, value: val } })}
                                                    disabled={disabled}
                                                    inputProps={{ step: 0.1 }}
                                                    name={property.name}
                                                    metricValue={property.value || ''}
                                                    InputProps={{ 
                                                        endAdornment: <Typography variant="caption" color="text.secondary">{property.flowUnit}</Typography> 
                                                    }}
                                                    helperText={renderErrorMessage(property.errorMessage, property.sectionNumber)}
                                                    error={!!property.errorMessage}
                                                    fullWidth
                                                    size="small"
                                                    tooltipContent={renderTooltipContent(property.lessThan, property.greaterThan, property.unitType)}
                                                />
                                        }
                                    </Box>
                                ))
                            }
                        </Box>

                        {/* Unit operation target value */}
                        <Box display="flex" flexDirection="column" gap={1}>
                            {
                                shouldDisplayUnitOperationTargetValue &&
                                <Box>
                                    <Typography variant="body2" gutterBottom>
                                        {unitOperationTargetValueProp?.property}
                                    </Typography>
                                    <UnitAwareTextField
                                        unitType={unitOperationTargetValueProp?.unitType ?? null}
                                        key={`${formKey}-${Constants.UnitOperationTargetValue}`}
                                        type='number'
                                        onMetricValueChange={(val) => handleChange({ target: { name: unitOperationTargetValueProp?.name, value: val } })}
                                        disabled={disabled}
                                        inputProps={{ step: 0.1 }}
                                        name={unitOperationTargetValueProp?.name}
                                        metricValue={unitOperationTargetValueProp?.value || ''}
                                        InputProps={{ 
                                            endAdornment: <Typography variant="caption" color="text.secondary">{unitOperationTargetValueProp?.flowUnit}</Typography> 
                                        }}
                                        helperText={renderErrorMessage(unitOperationTargetValueProp?.errorMessage)}
                                        error={!!unitOperationTargetValueProp?.errorMessage}
                                        fullWidth
                                        size="small"
                                        tooltipContent={renderTooltipContent(unitOperationTargetValueProp?.lessThan, unitOperationTargetValueProp?.greaterThan, unitOperationTargetValueProp?.unitType ?? null, undefined, unitOperationTargetValueProp?.flowUnit)}
                                    />
                                </Box>
                            }
                        </Box>
                    </>
            }

            {/* Temperature error alert */}
            {
                temperatureError && temperatureError.length > 0 &&
                <Box sx={{ width: '100%' }}>
                    <Alert severity="error">
                        {temperatureError.map((error, index) => (
                            <Typography key={index}>{error}</Typography>
                        ))}
                    </Alert>
                </Box>
            }

            {/* Design Range Feedback Dialog */}
            <RangeFeedbackDialog 
                open={feedbackDialogOpen}
                onClose={() => setFeedbackDialogOpen(false)}
            />
        </Stack>
    );
}
